import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, ImageIcon, LayoutGrid, Loader2, Search, Upload, UserPlus, Users, X } from "lucide-react";
import {
  Button,
  Dialog, DialogContent,
  ToggleGroup,
  ToggleGroupItem,
  UserAvatar,
} from "@nexus/ui";
import type { AddPreflightMarketInput } from "@nexus/contracts";
import { IndustryMultiSelect } from "./IndustryMultiSelect.js";
import { MarketMultiSelect } from "./MarketMultiSelect.js";
import { cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useProvisionTenant, useAllIndustries } from "@/features/identity-hub/hooks/useIdentityHub";
import { identityHubKeys } from "@/lib/queries/identity-hub.queries";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  adminUpdateTenantSettings,
  fetchAccessByTenant,
  grantUserAccess,
  pollStatus,
} from "@/features/identity-hub/api/identity-hub.api";
import type { UserItem, AccessGroupItem } from "@/features/identity-hub/api/identity-hub.api";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { BRAND_COLORS, translateBrandColorLabel } from "@/features/identity-hub/lib/brand-colors";
import { TENANT_LOGO_ACCEPT } from "@/features/identity-hub/lib/tenant-logo-upload-constraints";
import { useTenantLogoUpload } from "@/features/identity-hub/hooks/useTenantLogoUpload";
import { isValidUuid } from "@/features/identity-hub/utils/iam.utils";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";

// ─── Constants ────────────────────────────────────────────────────────────────

function useTenantTypes(): Array<{ value: "ORGANIZATION" | "API"; label: string }> {
  const { t } = useTranslation();
  return [
    { value: "ORGANIZATION", label: t("iam.newOrgDialog.tenantType.organization") },
    { value: "API", label: t("iam.newOrgDialog.tenantType.api") },
  ];
}

// ─── Org avatar preview ───────────────────────────────────────────────────────

// ─── MemberAssignment type ────────────────────────────────────────────────────

interface MemberAssignment {
  userId: string;
  roleName: string;
}

// ─── MemberCombobox ───────────────────────────────────────────────────────────

function MemberCombobox({
  users,
  onSelect,
}: {
  users: UserItem[];
  onSelect: (userId: string) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = query.trim().length > 0
    ? users
        .filter((u) => {
          const q = query.toLowerCase();
          return (
            u.displayName.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q)
          );
        })
        .slice(0, 7)
    : [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (userId: string) => {
    onSelect(userId);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { setOpen(false); setQuery(""); }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex h-9 items-center gap-2 rounded-md border bg-background px-3 transition-colors",
          open && filtered.length > 0
            ? "border-ring ring-1 ring-ring/40"
            : "border-border focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40",
        )}
      >
        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t("iam.newOrgDialog.membersSection.searchPlaceholder")}
          className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/50"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(""); setOpen(false); inputRef.current?.focus(); }}
            className="text-muted-foreground/60 hover:text-muted-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-md">
          {filtered.map((u) => (
            <div
              key={u.id}
              role="button"
              tabIndex={0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(u.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(u.id); } }}
              className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/60"
            >
              <ImagePreviewTrigger src={u.photoUrl} alt={u.displayName}>
                <UserAvatar name={u.displayName} size="sm" src={u.photoUrl} className="shrink-0" />
              </ImagePreviewTrigger>
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-[12px] font-medium leading-tight text-foreground">
                  {u.displayName}
                </span>
                <span className="truncate text-[11px] leading-tight text-muted-foreground">
                  {u.email}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MemberChip ───────────────────────────────────────────────────────────────

function MemberChip({
  user,
  roleName,
  roles,
  onRoleChange,
  onRemove,
}: {
  user: UserItem;
  roleName: string;
  roles: AccessGroupItem[];
  onRoleChange: (roleName: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2 transition-colors hover:bg-muted/30">
      <ImagePreviewTrigger src={user.photoUrl} alt={user.displayName}>
        <UserAvatar name={user.displayName} size="sm" src={user.photoUrl} className="shrink-0" />
      </ImagePreviewTrigger>

      <div className="flex min-w-0 flex-1 flex-col gap-0">
        <span className="truncate text-[12px] font-medium leading-tight text-foreground">
          {user.displayName}
        </span>
        <span className="truncate text-[10px] leading-tight text-muted-foreground">
          {user.email}
        </span>
      </div>

      {roles.length > 0 ? (
        <select
          value={roleName}
          onChange={(e) => onRoleChange(e.target.value)}
          className={cn(
            "h-6 appearance-none rounded-full border px-2.5 text-[10px] font-medium outline-none transition-colors cursor-pointer",
            roleName
              ? "border-primary/30 bg-primary/10 text-primary"
              : "border-border bg-muted text-muted-foreground hover:border-ring/50",
          )}
        >
          <option value="">{t("iam.newOrgDialog.membersSection.roleSelectPlaceholder")}</option>
          {roles.map((r) => (
            <option key={r.id} value={r.name}>{r.name}</option>
          ))}
        </select>
      ) : (
        <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
          {t("iam.newOrgDialog.membersSection.noRoles")}
        </span>
      )}

      <button
        type="button"
        onClick={onRemove}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground/50 transition-colors hover:bg-destructive/10 hover:text-destructive"
        aria-label={t("iam.newOrgDialog.membersSection.removeMemberAria", { name: user.displayName })}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── MembersSection ───────────────────────────────────────────────────────────

function MembersSection({
  allUsers,
  roles,
  assignments,
  onAdd,
  onRoleChange,
  onRemove,
}: {
  allUsers: UserItem[];
  roles: AccessGroupItem[];
  assignments: MemberAssignment[];
  onAdd: (userId: string) => void;
  onRoleChange: (i: number, roleName: string) => void;
  onRemove: (i: number) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const assignedIds = new Set(assignments.map((a) => a.userId));
  const availableUsers = allUsers.filter((u) => !assignedIds.has(u.id));

  return (
    <div className="flex flex-col rounded-xl border border-border">
      {/* Section toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-muted/30"
      >
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="flex flex-1 items-center gap-2">
          <span className="text-[12.5px] font-medium text-foreground">{t("iam.newOrgDialog.membersSection.title")}</span>
          {assignments.length > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/10 px-1 text-[9.5px] font-semibold tabular-nums text-primary">
              {assignments.length}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground/60">{t("iam.newOrgDialog.membersSection.optional")}</span>
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
            expanded && "rotate-180",
          )}
        />
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="flex flex-col gap-3 border-t border-border px-4 pb-4 pt-3">
          <MemberCombobox users={availableUsers} onSelect={onAdd} />

          {assignments.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-border bg-muted/40">
                <UserPlus className="h-4 w-4 text-muted-foreground/60" />
              </div>
              <div className="text-center">
                <p className="text-[12px] font-medium text-muted-foreground">{t("iam.newOrgDialog.membersSection.emptyTitle")}</p>
                <p className="text-[11px] text-muted-foreground/60">
                  {t("iam.newOrgDialog.membersSection.emptyHint")}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {assignments.map((a, i) => {
                const user = allUsers.find((u) => u.id === a.userId);
                if (!user) return null;
                return (
                  <MemberChip
                    key={a.userId}
                    user={user}
                    roleName={a.roleName}
                    roles={roles}
                    onRoleChange={(roleName) => onRoleChange(i, roleName)}
                    onRemove={() => onRemove(i)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── NewOrgDialog ─────────────────────────────────────────────────────────────

interface NewOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  superTenantId: string | null;
  allUsers: UserItem[];
  superTenantRoles: AccessGroupItem[];
}

export function NewOrgDialog({
  open,
  onOpenChange,
  superTenantId,
  allUsers,
  superTenantRoles,
}: NewOrgDialogProps) {
  const { t } = useTranslation();
  const tenantTypes = useTenantTypes();

  // ── Form state ──────────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [type, setType] = useState<"ORGANIZATION" | "API">("ORGANIZATION");
  const [industryIndices, setIndustryIndices] = useState<string[]>([]);
  const [preflightMarketRefs, setPreflightMarketRefs] = useState<AddPreflightMarketInput[]>([]);
  const [defaultIndustry, setDefaultIndustry] = useState<string | null>(null);
  const [defaultBrand, setDefaultBrand] = useState("");
  const [color, setColor] = useState(BRAND_COLORS[0].value);
  const [description, setDescription] = useState("");
  const [retoolUserId, setRetoolUserId] = useState("");
  const [retoolTouched, setRetoolTouched] = useState(false);
  const [memberAssignments, setMemberAssignments] = useState<MemberAssignment[]>([]);
  const [isGrantingAccess, setIsGrantingAccess] = useState(false);

  // ── Logo upload state ───────────────────────────────────────────────────────
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const logoUpload = useTenantLogoUpload({
    tenantId: activeTenantId,
    onUploaded: (confirmed) => setLogoUrl(confirmed.publicUrl ?? null),
  });
  const { previewUrl: logoPreviewUrl, progress: logoUploadProgress, inputRef: logoInputRef } = logoUpload;

  const qc = useQueryClient();
  const provisionTenant = useProvisionTenant();
  const { data: industries = [] } = useAllIndustries();
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const hasPermission = perms.IDENTITY_HUB_CREATE_TENANT.isOn && isSuperTenant;
  const isLogoUploading = logoUpload.isUploading;

  // ── Validation ──────────────────────────────────────────────────────────────
  const nameTrimmed = name.trim();
  const nameError = nameTrimmed.length === 0 ? null : nameTrimmed.length > 255 ? t("iam.validation.nameTooLong") : null;
  const descError = description.length > 500 ? t("iam.validation.descTooLong") : null;
  const retoolError = retoolUserId && !isValidUuid(retoolUserId)
    ? t("iam.validation.invalidUuid")
    : null;

  const canSubmit =
    nameTrimmed.length > 0 &&
    !nameError &&
    !descError &&
    !retoolError &&
    !!superTenantId &&
    hasPermission &&
    !provisionTenant.isPending &&
    !isGrantingAccess &&
    !isLogoUploading;

  // ── Reset ───────────────────────────────────────────────────────────────────
  const reset = () => {
    setName("");
    setType("ORGANIZATION");
    setIndustryIndices([]);
    setPreflightMarketRefs([]);
    setDefaultIndustry(null);
    setColor(BRAND_COLORS[0].value);
    setDescription("");
    setRetoolUserId("");
    setRetoolTouched(false);
    setMemberAssignments([]);
    setIsGrantingAccess(false);
    setLogoUrl(null);
    logoUpload.clearPreview();
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleLogoRemove = () => {
    logoUpload.clearPreview();
    setLogoUrl(null);
  };

  // ── Members ─────────────────────────────────────────────────────────────────
  const addMember = (userId: string) =>
    setMemberAssignments((prev) => [...prev, { userId, roleName: "" }]);

  const updateMemberRole = (i: number, roleName: string) =>
    setMemberAssignments((prev) =>
      prev.map((m, idx) => (idx === i ? { ...m, roleName } : m)),
    );

  const removeMember = (i: number) =>
    setMemberAssignments((prev) => prev.filter((_, idx) => idx !== i));

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!superTenantId || !hasPermission) return;
    setRetoolTouched(true);
    if (retoolError) return;
    try {
      const result = await provisionTenant.mutateAsync({
        name: nameTrimmed,
        type,
        tenantId: superTenantId,
        copyAccessFromTenantId: superTenantId,
        retoolUserId: retoolUserId.trim() || null,
        ...(description.trim() ? { description: description.trim() } : {}),
        brandColor: color,
        ...(logoUrl ? { logoUrl } : {}),
        ...(industryIndices.length > 0 ? { industryIndices } : {}),
        ...(preflightMarketRefs.length > 0 ? { preflightMarketRefs } : {}),
      });

      const tenantId = result.tenantId as string | undefined;
      const effectiveDefault = industryIndices.includes(defaultIndustry ?? "")
        ? defaultIndustry
        : industryIndices.length === 1
          ? industryIndices[0]!
          : null;

      const trimmedBrand = defaultBrand.trim();
      const settingsPayload: { defaultIndustry?: string | null; defaultBrand?: string | null } = {};
      if (effectiveDefault) settingsPayload.defaultIndustry = effectiveDefault;
      if (trimmedBrand) settingsPayload.defaultBrand = trimmedBrand;

      if (tenantId && Object.keys(settingsPayload).length > 0) {
        try {
          await adminUpdateTenantSettings(tenantId, settingsPayload);
          await qc.invalidateQueries({ queryKey: identityHubKeys.all });
        } catch {
          // best-effort — org created, defaults can be set later in settings
        }
      }

      const validAssignments = memberAssignments.filter((m) => m.userId && m.roleName);
      if (validAssignments.length > 0) {
        if (tenantId) {
          setIsGrantingAccess(true);
          try {
            const newRoles = await fetchAccessByTenant(tenantId);
            const roleNameToId = new Map(newRoles.map((r) => [r.name, r.id]));
            const grants = await Promise.allSettled(
              validAssignments.map(({ userId, roleName }) => {
                const accessId = roleNameToId.get(roleName);
                if (!accessId) return Promise.resolve(null);
                return grantUserAccess({ userId, accessId, tenantId });
              }),
            );
            // Poll each amber correlationId for completion before invalidating
            await Promise.allSettled(
              grants.map((g) =>
                g.status === "fulfilled" && g.value
                  ? pollStatus("/users", g.value.correlationId)
                  : Promise.resolve(),
              ),
            );
            await qc.invalidateQueries({ queryKey: identityHubKeys.users() });
            await qc.invalidateQueries({ queryKey: identityHubKeys.all });
            toast.success(t("iam.toast.membersAdded", { count: validAssignments.length }));
          } catch {
            // best-effort — org created, members can be added later
          } finally {
            setIsGrantingAccess(false);
          }
        }
      }

      handleOpenChange(false);
    } catch {
      // onError toast shown by useProvisionTenant
    }
  };

  const isPending = provisionTenant.isPending || isGrantingAccess;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-[560px] gap-0 overflow-hidden p-0">
        {/* ── Header ─────────────────────────────────────────── */}
        <DialogIconHeader
          icon={LayoutGrid}
          title={t("iam.newOrgDialog.title")}
          description={t("iam.newOrgDialog.description")}
        />

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="flex max-h-[70vh] flex-col gap-5 overflow-y-auto px-6 py-5">
          {/* Display name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">
              {t("iam.newOrgDialog.displayNameLabel")} <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              placeholder={t("iam.newOrgDialog.displayNamePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              className={cn(
                "h-9 rounded-md border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1 focus:ring-ring/40",
                nameError ? "border-destructive focus:border-destructive" : "border-border focus:border-ring",
              )}
              autoFocus
            />
            {nameError && <p className="text-[11px] text-destructive">{nameError}</p>}
          </div>

          {/* Type */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">
              {t("iam.newOrgDialog.typeLabel")} <span className="text-destructive">*</span>
            </label>
            <ToggleGroup
              type="single"
              value={type}
              onValueChange={(v) => v && setType(v as "ORGANIZATION" | "API")}
              size="sm"
              className="w-fit"
            >
              {tenantTypes.map((tt) => (
                <ToggleGroupItem
                  key={tt.value}
                  value={tt.value}
                  className="h-7 min-w-[5.5rem] justify-center px-2 text-[11px] font-medium"
                >
                  {tt.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {/* Industries */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">{t("iam.newOrgDialog.industriesLabel")}</label>
            <IndustryMultiSelect
              selected={industryIndices}
              onChange={(next) => {
                setIndustryIndices(next);
                if (defaultIndustry && !next.includes(defaultIndustry)) {
                  setDefaultIndustry(next.length === 1 ? next[0]! : null);
                }
              }}
              industries={industries}
              placeholder={t("iam.newOrgDialog.industriesPlaceholder")}
            />
          </div>

          {/* Pre Flight markets — catalogue entries; Global is granted to every tenant */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">
              {t("iam.newOrgDialog.marketsLabel")}
              <span className="ml-1 font-normal text-muted-foreground/70">{t("iam.newOrgDialog.marketsHint")}</span>
            </label>
            <MarketMultiSelect
              selected={preflightMarketRefs}
              onChange={setPreflightMarketRefs}
              placeholder={t("iam.newOrgDialog.marketsSelectPlaceholder", "Select markets…")}
              ariaLabel={t("iam.newOrgDialog.marketsLabel")}
            />
          </div>

          {/* Default industry — only shown when 2+ industries are assigned */}
          {industryIndices.length >= 2 && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                {t("iam.newOrgDialog.defaultIndustryLabel")}
                <span className="ml-1 font-normal text-muted-foreground/70">{t("iam.newOrgDialog.defaultIndustryHint")}</span>
              </label>
              <select
                value={defaultIndustry ?? ""}
                onChange={(e) => setDefaultIndustry(e.target.value || null)}
                className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40"
              >
                <option value="">{t("iam.shared.none")}</option>
                {industries
                  .filter((ind) => industryIndices.includes(ind.index))
                  .map((ind) => (
                    <option key={ind.index} value={ind.index}>{ind.title}</option>
                  ))}
              </select>
            </div>
          )}

          {/* Default brand — marketing brand name sent to Pre Flight creative analysis */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">
              {t("iam.newOrgDialog.defaultBrandLabel", "Default brand")}
              <span className="ml-1 font-normal text-muted-foreground/70">{t("iam.newOrgDialog.defaultBrandHint", "Optional · analysed in Pre Flight")}</span>
            </label>
            <input
              type="text"
              placeholder={t("iam.newOrgDialog.defaultBrandPlaceholder", "e.g. Vodafone")}
              value={defaultBrand}
              onChange={(e) => setDefaultBrand(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40"
            />
          </div>

          {/* ── Members ──────────────────────────────────────── */}
          <MembersSection
            allUsers={allUsers}
            roles={superTenantRoles}
            assignments={memberAssignments}
            onAdd={addMember}
            onRoleChange={updateMemberRole}
            onRemove={removeMember}
          />

          {/* Brand color */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-foreground">{t("iam.newOrgDialog.brandColorLabel")}</label>
            <div className="flex items-center gap-2 flex-wrap">
              {BRAND_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  aria-label={translateBrandColorLabel(t, c.value, c.label)}
                  onClick={() => setColor(c.value)}
                  className={cn(
                    "relative h-9 w-9 rounded-lg transition-all",
                    color === c.value && "ring-2 ring-offset-2 ring-ring",
                  )}
                  style={{ backgroundColor: c.value }}
                >
                  {color === c.value && (
                    <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-sm" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Logo upload */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">{t("iam.newOrgDialog.logoLabel")}</label>
            <div className="flex items-start gap-3">
              <div className="relative shrink-0">
                {logoPreviewUrl ? (
                  <img
                    src={logoPreviewUrl}
                    alt={t("iam.newOrgDialog.logoPreviewAlt")}
                    className="h-14 w-14 rounded-lg border border-border/60 bg-background object-contain"
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted text-muted-foreground"
                  >
                    <ImageIcon className="h-5 w-5 opacity-40" />
                  </div>
                )}
                {isLogoUploading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/80">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept={TENANT_LOGO_ACCEPT}
                    className="sr-only"
                    disabled={isLogoUploading}
                    onChange={(e) => void logoUpload.handleFileChange(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    disabled={isLogoUploading}
                    onClick={() => logoInputRef.current?.click()}
                    className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Upload className="h-3 w-3" />
                    {isLogoUploading ? t("iam.orgDrawer.settings.logoUploading") : t("iam.orgDrawer.settings.logoUpload")}
                  </button>
                  {logoPreviewUrl && !isLogoUploading && (
                    <button
                      type="button"
                      onClick={handleLogoRemove}
                      className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                      {t("iam.orgDrawer.settings.logoRemove")}
                    </button>
                  )}
                </div>
                {isLogoUploading && (
                  <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-200"
                      style={{ width: `${logoUploadProgress ?? 0}%` }}
                    />
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">
                  {t("iam.orgDrawer.settings.logoHint")}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">{t("iam.newOrgDialog.descriptionLabel")}</label>
            <textarea
              placeholder={t("iam.newOrgDialog.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
              className={cn(
                "resize-none rounded-md border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1 focus:ring-ring/40",
                descError ? "border-destructive focus:border-destructive" : "border-border focus:border-ring",
              )}
            />
            <div className="flex items-center justify-between">
              {descError
                ? <p className="text-[11px] text-destructive">{descError}</p>
                : <p className="text-[11px] text-muted-foreground">{t("iam.newOrgDialog.descriptionOptionalHint")}</p>
              }
              <span className="text-[11px] text-muted-foreground tabular-nums">{description.length}/500</span>
            </div>
          </div>

          {/* Retool user ID */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground">
              {t("iam.newOrgDialog.retoolUserIdLabel")}
            </label>
            <input
              type="text"
              placeholder={t("iam.newOrgDialog.retoolUserIdPlaceholder")}
              value={retoolUserId}
              onChange={(e) => setRetoolUserId(e.target.value.trim())}
              onBlur={() => setRetoolTouched(true)}
              className={cn(
                "h-9 rounded-md border bg-background px-3 font-mono text-xs text-foreground outline-none placeholder:font-sans placeholder:text-muted-foreground/60 focus:ring-1 focus:ring-ring/40",
                retoolTouched && retoolError ? "border-destructive focus:border-destructive" : "border-border focus:border-ring",
              )}
            />
            {retoolTouched && retoolError
              ? <p className="text-[11px] text-destructive">{retoolError}</p>
              : <p className="text-[11px] text-muted-foreground">{t("iam.newOrgDialog.retoolUserIdOptionalHint")}</p>
            }
          </div>

        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground">{t("iam.shared.pressEscToCancel")}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!canSubmit}
              onClick={() => void handleSubmit()}
            >
              {isPending
                ? isGrantingAccess ? t("iam.newOrgDialog.addingMembers") : t("iam.newOrgDialog.creating")
                : t("iam.newOrgDialog.submit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
